import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import ConfirmationPopup, {
  DIALOG_TYPES,
} from "../../packages/ConfirmationPopup";
import WPButton from "../../packages/WPButton";

import TooltipRenderer from "../TooltipRenderer";

import { CustomModalButtons } from "../../constants/global.constants";

import {
  CalendarIcon,
  DeleteIcon,
  ExcelIcon,
  Timeline,
} from "../../assets/icons";

import { combineClassNames } from "../../utils/appUtils";
import { ValueStrings } from "../../utils/constantsString";
import { formatDate, getUploadedDaysAgo } from "../../utils/Utils";

import "./FileMetaCard.scss";

/**
 * FileMetaCard Component
 * Displays a file upload item with timeframe, file details, and action buttons
 * Used in data append/upload workflows
 *
 * @param {Object} props
 * @param {string} [props.fileName] - Name of the uploaded file
 * @param {string} [props.startDate] - Start date of timeframe (format: DD MMM YYYY)
 * @param {string} [props.endDate] - End date of timeframe (format: DD MMM YYYY)
 * @param {number} [props.recordCount] - Number of records in the file
 * @param {string} [props.uploadedBy] - Email of user who uploaded the file
 * @param {string} [props.uploadedDaysAgo] - Relative upload time (e.g., "12 days ago")
 * @param {Function} [props.onViewData] - Callback when View Data button is clicked
 * @param {Function} [props.onDelete] - Callback when Delete button is clicked
 * @param {string} [props.className] - Additional CSS classes
 */
const FileMetaCard = ({
  fileName,
  startDate,
  endDate,
  recordCount,
  uploadedBy,
  appendedAt,
  onViewData,
  onDelete,
  className,
}) => {
  const { t } = useTranslation();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const onRemoveConfirmationClose = (action) => {
    setShowDeleteConfirmation(false);
    if (action === CustomModalButtons.CONFIRM) {
      if (onDelete) {
        onDelete();
      }
    }
  };

  const uploadedDaysAgo = useMemo(() => {
    return getUploadedDaysAgo(t, appendedAt);
  }, [t, appendedAt]);

  return (
    <div className={combineClassNames("file-meta-card-wrapper", className)}>
      <div className="file-meta-card-timeframe">
        <div className="timeframe-date-group">
          <CalendarIcon aria-label="Calendar Icon" className="calendar-icon" />
          <span className="timeframe-date">
            {formatDate(startDate, ValueStrings.dateFormatMonthAbbr)}
          </span>
        </div>
        <Timeline aria-label="Timeline Icon" height={12} />
        <div className="timeframe-date-group">
          <span className="timeframe-date">
            {formatDate(endDate, ValueStrings.dateFormatMonthAbbr)}
          </span>
        </div>
      </div>

      <div className="file-meta-card">
        <div className="file-info-section">
          <ExcelIcon aria-label="Excel File Icon" className="file-icon" />
          <TooltipRenderer title={fileName}>
            <span className="file-name">{fileName}</span>
          </TooltipRenderer>
        </div>

        <div className="upload-details-section">
          <div className="upload-details-row">
            <span className="upload-label">{t("dataAppend.appended")}</span>
            <span className="upload-time">{uploadedDaysAgo}</span>
          </div>
          <div className="upload-details-row">
            <span className="upload-by">{t("ValueStrings.by")}</span>
            <TooltipRenderer title={uploadedBy}>
              <span className="upload-email">{uploadedBy}</span>
            </TooltipRenderer>
          </div>
        </div>

        <div className="records-section">
          <div className="records-count-container">
            <TooltipRenderer title={recordCount}>
              <span className="records-count">{recordCount}</span>
            </TooltipRenderer>
          </div>
          <span className="records-label">{t("ValueStrings.records")}</span>
        </div>

        <div className="actions-section">
          <WPButton
            variant="text"
            type="primary"
            size="small"
            onClick={onViewData}
            customClass="view-data-button"
          >
            {t("analyseData.viewData")}
          </WPButton>

          {onDelete && (
            <WPButton
              variant="text"
              customClass="delete-button"
              onClick={() => setShowDeleteConfirmation(true)}
            >
              <DeleteIcon
                aria-label="Delete Icon"
                className="delete-icon"
                color="var(--color-red)"
              />
            </WPButton>
          )}

          {showDeleteConfirmation && (
            <ConfirmationPopup
              header={t("dataAppend.deleteAppendedDataHeader")}
              description={t("dataAppend.deleteAppendedDataBody")}
              type={DIALOG_TYPES.ERROR}
              onClose={onRemoveConfirmationClose}
              confirmBtn={t("ValueStrings.delete")}
              cancelBtn={t("ValueStrings.cancel")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default FileMetaCard;
